import React, { useState, useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../../firebase.init";

const SubscriptionHistory = () => {
  const [user] = useAuthState(auth);
  const [orders, setOrders] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "date", // Changed from orderDate to date (generic key)
    direction: "desc", // Default to descending to show newest first
  });
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        if (!user?.email) {
          throw new Error("User email not found");
        }

        // Fetch user-specific orders
        const ordersResponse = await fetch(
          `https://allinoneai-d1a5a1b54c64.herokuapp.com/orders?userEmail=${user.email}`
        );
        if (!ordersResponse.ok) {
          throw new Error(`HTTP error! Status: ${ordersResponse.status}`);
        }
        const ordersData = await ordersResponse.json();

        // Sort orders by date immediately (latest first)
        const sortedOrders = ordersData.sort((a, b) => {
          // Get date from either createdAt, orderDate or any available date field
          const dateA = new Date(a.createdAt || a.orderDate || a.date || 0);
          const dateB = new Date(b.createdAt || b.orderDate || b.date || 0);
          return dateB - dateA; // Sort in descending order (newest first)
        });

        // Fetch packages
        const packagesResponse = await fetch("https://allinoneai-d1a5a1b54c64.herokuapp.com/packages");
        if (!packagesResponse.ok) {
          throw new Error(`HTTP error! Status: ${packagesResponse.status}`);
        }
        const packagesData = await packagesResponse.json();

        // Set the fetched data
        setOrders(sortedOrders);
        setPackages(packagesData);
        setError(null);
      } catch (err) {
        setError(`Failed to fetch data: ${err.message}`);
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  // Get package name by ID
  const getPackageName = (packageId) => {
    const packageItem = packages.find((pkg) => pkg._id === packageId);
    return packageItem ? packageItem.packageName : "N/A";
  };

  // Open package modal
  const openPackageModal = (packageId) => {
    const packageItem = packages.find((pkg) => pkg._id === packageId);
    if (packageItem) {
      setSelectedPackage(packageItem);
      setShowModal(true);
    }
  };

  // Close package modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedPackage(null);
  };

  // Calculate days left in membership
  const calculateDaysLeft = (validityDate) => {
    if (!validityDate) return "N/A";

    const today = new Date();
    const endDate = new Date(validityDate);
    const timeDiff = endDate - today;
    const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    return daysLeft > 0 ? daysLeft : "Expired";
  };

  // Get date value for sorting from order object
  const getOrderDate = (order) => {
    // Try different date fields, with a fallback
    return order.createdAt || order.orderDate || order.date || "1970-01-01";
  };

  // Sorting function
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Get sorted and filtered orders
  const getSortedAndFilteredOrders = () => {
    let filteredOrders = [...orders];

    // Apply search filter
    if (searchTerm) {
      filteredOrders = filteredOrders.filter(
        (order) =>
          order.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.orderStatus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          getPackageName(order.packageId)
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filteredOrders.sort((a, b) => {
        if (sortConfig.key === "date") {
          // Handle date sorting specifically
          const dateA = new Date(getOrderDate(a));
          const dateB = new Date(getOrderDate(b));
          return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
        }

        if (sortConfig.key === "daysLeft") {
          const daysLeftA = calculateDaysLeft(a.MembershipValidity);
          const daysLeftB = calculateDaysLeft(b.MembershipValidity);

          // Handle 'Expired' case
          if (daysLeftA === "Expired" && daysLeftB !== "Expired")
            return sortConfig.direction === "asc" ? -1 : 1;
          if (daysLeftA !== "Expired" && daysLeftB === "Expired")
            return sortConfig.direction === "asc" ? 1 : -1;
          if (daysLeftA === "Expired" && daysLeftB === "Expired") return 0;

          return sortConfig.direction === "asc"
            ? daysLeftA - daysLeftB
            : daysLeftB - daysLeftA;
        }

        if (sortConfig.key === "packageName") {
          const packageNameA = getPackageName(a.packageId);
          const packageNameB = getPackageName(b.packageId);

          if (packageNameA < packageNameB) {
            return sortConfig.direction === "asc" ? -1 : 1;
          }
          if (packageNameA > packageNameB) {
            return sortConfig.direction === "asc" ? 1 : -1;
          }
          return 0;
        }

        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredOrders;
  };

  // Get current orders for pagination
  const sortedAndFilteredOrders = getSortedAndFilteredOrders();
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = sortedAndFilteredOrders.slice(
    indexOfFirstOrder,
    indexOfLastOrder
  );
  const totalPages = Math.ceil(sortedAndFilteredOrders.length / ordersPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Function to determine badge color based on order status
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Completed":
        return "bg-success";
      case "Processing":
        return "bg-warning text-white";
      case "Shipped":
        return "bg-info text-white";
      case "Cancelled":
        return "bg-danger";
      case "Paid":
        return "bg-success";
      case "pending":
        return "bg-warning text-white";
      default:
        return "bg-secondary";
    }
  };

  // Function to determine badge color based on days left
  const getValidityBadgeClass = (daysLeft) => {
    if (daysLeft === "Expired") return "bg-danger";
    if (daysLeft === "N/A") return "bg-secondary";

    const days = parseInt(daysLeft);
    if (days <= 3) return "bg-danger";
    if (days <= 7) return "bg-warning text-white";
    return "bg-success";
  };

  // Format date to readable format
  const formatDate = (dateString) => {
    try {
      if (!dateString) return "N/A";

      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return dateString;

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return dateString || "N/A";
    }
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? " ↑" : " ↓";
    }
    return "";
  };

  return (
    <div className="premium-app-container py-4">
      <div className="row mb-4">
        <div className="col">
          <h3 className="fw-bold text-white">
            Subscription History
          </h3>
          <p className="text-white-50">View and manage all Subscription History</p>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col">
          <div
            className="glass-card border-0"
            style={{ borderRadius: "10px" }}
          >
            <div
              className="card-header d-flex justify-content-between align-items-center py-3"
              style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", backgroundColor: "transparent" }}
            >
              <h5 className="mb-0 fw-bold text-white">
                All Orders
              </h5>
              <div className="d-flex">
                <div className="input-group me-2" style={{ maxWidth: "250px" }}>
                  <input
                    type="text"
                    className="form-control border-end-0 bg-dark text-white border-secondary"
                    placeholder="Search orders..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1); // Reset to first page on search
                    }}
                    style={{ borderRadius: "5px 0 0 5px" }}
                  />
                  <span
                    className="input-group-text bg-dark border-start-0 border-secondary"
                    style={{ borderRadius: "0 5px 5px 0" }}
                  >
                    <i className="fa fa-search text-white-50"></i>
                  </span>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden"></span>
                  </div>
                  <p className="mt-2 text-white-50">Loading orders...</p>
                </div>
              ) : error ? (
                <div className="alert alert-danger m-3" role="alert">
                  {error}
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table
                      className="table table-dark table-hover mb-0"
                      style={{ borderCollapse: "separate", borderSpacing: "0" }}
                    >
                      <thead>
                        <tr className="text-white text-uppercase small border-bottom border-secondary">
                          <th
                            className="px-4 py-3 text-white"
                            style={{ cursor: "pointer" }}
                            onClick={() => requestSort("orderId")}
                          >
                            Order ID {getSortIndicator("orderId")}
                          </th>

                          <th
                            className="px-4 py-3 text-white"
                            style={{ cursor: "pointer" }}
                            onClick={() => requestSort("packageName")}
                          >
                            Package {getSortIndicator("packageName")}
                          </th>
                          <th
                            className="px-4 py-3 text-white"
                            style={{ cursor: "pointer" }}
                            onClick={() => requestSort("date")}
                          >
                            Date {getSortIndicator("date")}
                          </th>
                          <th
                            className="px-4 py-3 text-white"
                            style={{ cursor: "pointer" }}
                            onClick={() => requestSort("totalPrice")}
                          >
                            Amount {getSortIndicator("totalPrice")}
                          </th>
                          <th
                            className="px-4 py-3 text-white"
                            style={{ cursor: "pointer" }}
                            onClick={() => requestSort("paymentStatus")}
                          >
                            Payment {getSortIndicator("paymentStatus")}
                          </th>
                          {/* <th
                            className="px-4 py-3 text-white"
                            style={{ cursor: "pointer" }}
                            onClick={() => requestSort("daysLeft")}
                          >
                            Validity {getSortIndicator("daysLeft")}
                          </th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {currentOrders.length === 0 ? (
                          <tr>
                            <td
                              colSpan="7"
                              className="text-center py-4 text-white-50"
                            >
                              No orders found
                            </td>
                          </tr>
                        ) : (
                          currentOrders.map((order) => {
                            const daysLeft = calculateDaysLeft(
                              order.MembershipValidity
                            );
                            return (
                              <tr
                                key={order._id}
                                style={{ transition: "all 0.2s ease" }}
                              >
                                <td className="px-4 py-3 align-middle fw-bold">
                                  #{order.orderId}
                                </td>

                                <td className="px-4 py-3 align-middle">
                                  {order.packageId ? (
                                    <span
                                      className="text-primary fw-bold"
                                      style={{
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                      }}
                                      onClick={() =>
                                        openPackageModal(order.packageId)
                                      }
                                    >
                                      {getPackageName(order.packageId)}
                                    </span>
                                  ) : (
                                    <span className="text-white-50">N/A</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 align-middle">
                                  {formatDate(getOrderDate(order))}
                                </td>
                                <td className="px-4 py-3 align-middle fw-bold">
                                  ${order.totalPrice} USD
                                </td>
                                <td className="px-4 py-3 align-middle">
                                  <span
                                    className={`badge ${getStatusBadgeClass(
                                      order.paymentStatus
                                    )} rounded-pill px-3 py-2`}
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    {order.paymentStatus.toUpperCase()}
                                  </span>
                                </td>
                                {/* <td className="px-4 py-3 align-middle">
                                  <span
                                    className={`badge ${getValidityBadgeClass(
                                      daysLeft
                                    )} rounded-pill px-3 py-2`}
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    {daysLeft === "Expired" ||
                                    daysLeft === "N/A"
                                      ? daysLeft
                                      : `${daysLeft} days left`}
                                  </span>
                                </td> */}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex justify-content-between align-items-center p-3 border-top">
                    <div className="text-white-50">
                      Showing {indexOfFirstOrder + 1} to{" "}
                      {Math.min(
                        indexOfLastOrder,
                        sortedAndFilteredOrders.length
                      )}{" "}
                      of {sortedAndFilteredOrders.length} entries
                    </div>
                    <nav aria-label="Page navigation">
                      <ul className="pagination mb-0">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link bg-dark text-white border-secondary"
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            style={{ border: "none" }}
                          >
                            Previous
                          </button>
                        </li>
                        {Array.from({ length: totalPages }).map((_, index) => (
                          <li
                            key={index}
                            className={`page-item ${
                              currentPage === index + 1 ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link bg-dark text-white border-secondary"
                              onClick={() => paginate(index + 1)}
                              style={{ border: "none" }}
                            >
                              {index + 1}
                            </button>
                          </li>
                        ))}
                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link bg-dark text-white border-secondary"
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            style={{ border: "none" }}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Package Modal */}
      {showModal && selectedPackage && (
        <div
          className="modal fade show"
          tabIndex="-1"
          role="dialog"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedPackage.packageName} Package Details
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="text-center mb-4">
                      <h3 className="display-6 fw-bold text-primary">
                        ${selectedPackage.price} USD
                      </h3>
                      <p className="text-white-50 mb-0">
                        {selectedPackage.credit} credits
                      </p>
                    </div>

                    <ul className="list-group list-group-flush">
                      <li className="list-group-item border-0 py-2">
                        <i className="fa fa-check text-success me-2"></i>{" "}
                        {selectedPackage.pointOne}
                      </li>
                      <li className="list-group-item border-0 py-2">
                        <i className="fa fa-check text-success me-2"></i>{" "}
                        {selectedPackage.pointTwo}
                      </li>
                      <li className="list-group-item border-0 py-2">
                        <i className="fa fa-check text-success me-2"></i>{" "}
                        {selectedPackage.pointThree}
                      </li>
                      <li className="list-group-item border-0 py-2">
                        <i className="fa fa-check text-success me-2"></i>{" "}
                        {selectedPackage.pointFour}
                      </li>
                      <li className="list-group-item border-0 py-2">
                        <i className="fa fa-check text-success me-2"></i>{" "}
                        {selectedPackage.pointFive}
                      </li>
                      <li className="list-group-item border-0 py-2">
                        <i className="fa fa-check text-success me-2"></i>{" "}
                        {selectedPackage.pointSix}
                      </li>
                    </ul>

                    <div className="d-flex justify-content-between mt-3">
                      <small className="text-white-50">
                        Package ID: {selectedPackage._id}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop for clicking outside */}
      {showModal && (
        <div className="modal-backdrop fade show" onClick={closeModal}></div>
      )}
    </div>
  );
};

export default SubscriptionHistory;
